import { useEffect, useState } from "react";
import { getAdminAuditActions, getAdminAuditLogs,verifyAdminAuditChain } from "../api/adminAuditLogs";
import AdminNav from "../components/AdminNav";
import PlatformAdminNav from "../components/PlatformAdminNav";
function formatDate(value) {
  if (!value) return "—";

  return new Date(value).toLocaleString();
}

function formatAction(action) {
  return String(action || "unknown")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function safeJson(value) {
  if (!value) return null;

  try {
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    return value;
  }
}

function getInmateName(log) {
  const inmate = log.legal_document?.inmate;

  if (!inmate) return "—";

  return `${inmate.first_name || ""} ${inmate.last_name || ""}`.trim() || "—";
}

function getUser() {
  const raw = localStorage.getItem("legal_portal_user");
  return raw ? JSON.parse(raw) : null;
}

export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    action: "",
    date_from: "",
    date_to: "",
    page: 1,
    per_page: 25,
  });

  const currentUser = getUser();

  async function loadLogs(nextFilters = filters) {
    setLoading(true);

    try {
      const params = {};

      Object.entries(nextFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params[key] = value;
        }
      });

      const res = await getAdminAuditLogs(params);
      const payload = res.data?.data;

      setLogs(payload?.data || []);
      setPagination(payload || null);
    } catch (err) {
      window.alert(err.response?.data?.message || "Failed to load audit logs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    async function loadActions() {
      try {
        const res = await getAdminAuditActions();
        setActions(res.data?.data || []);
      } catch {
        setActions([]);
      }
    }

    loadActions();
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: 1,
    }));
  };

  const applyFilters = (e) => {
    e.preventDefault();

    const nextFilters = {
      ...filters,
      page: 1,
    };

    setFilters(nextFilters);
    loadLogs(nextFilters);
  };

  const goToPage = (page) => {
    const nextFilters = {
      ...filters,
      page,
    };

    setFilters(nextFilters);
    loadLogs(nextFilters);
  };

  const clearFilters = () => {
    const nextFilters = {
      search: "",
      action: "",
      date_from: "",
      date_to: "",
      page: 1,
      per_page: 25,
    };

    setFilters(nextFilters);
    loadLogs(nextFilters);
  };

const verifyAuditChain = async () => {
  setVerifyLoading(true);
  setVerifyResult(null);

  try {
    const res = await verifyAdminAuditChain();
    setVerifyResult(res.data?.data || null);
  } catch (err) {
    window.alert(err.response?.data?.message || "Unable to verify audit chain.");
  } finally {
    setVerifyLoading(false);
  }
};

return (
  <div className="app-shell">
    <div className="topbar d-flex justify-content-between align-items-center">
      <div className="d-flex align-items-center gap-3">
        <div className="brand-mark">CV</div>

        <div>
          <h5 className="mb-0">CounselVault</h5>
          <div className="small opacity-75">
            Security & Audit Monitoring
          </div>
        </div>
      </div>
	{currentUser?.role === "platform_admin" ? <PlatformAdminNav /> : <AdminNav />}
    </div>

    <main className="page-wrap">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">Audit Trail</h2>
          <div className="text-muted">
            Review security, access, login, MFA, attorney, inmate, and document activity.
          </div>
        </div>

        <div className="audit-summary-card">
          <div className="text-muted small">Total Events</div>
          <div className="fw-bold fs-4">{pagination?.total ?? "—"}</div>
        </div>
      </div>
	<div className="audit-integrity-card mb-4">
	  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
	    <div>
	      <h6 className="fw-bold mb-1">Audit Integrity Check</h6>
	      <div className="text-muted small">
		  Verifies the tamper-evident hash chain for all audit events visible to your role.
		  Facility admins see facility-scoped events. Platform admins verify the global system audit chain.
	      </div>
	    </div>

	    <button
	      type="button"
	      className="btn btn-sm btn-dark"
	      disabled={verifyLoading}
	      onClick={verifyAuditChain}
	    >
	      {verifyLoading ? "Checking..." : "Verify Integrity"}
	    </button>
	  </div>

	  {verifyResult && (
	    <div className={`mt-3 alert ${verifyResult.valid ? "alert-success" : "alert-danger"} py-2 mb-0`}>
	      {verifyResult.valid
		? `Integrity verified. ${verifyResult.checked} chained event(s) checked with no issues.`
		: `Verification failed. ${verifyResult.issues?.length || 0} integrity issue(s) found.`}
	    </div>
	  )}
	</div>
      <form className="audit-filter-card mb-4" onSubmit={applyFilters}>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Search</label>
            <input
              className="form-control"
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
              placeholder="User, inmate, action, IP, document..."
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Action</label>
            <select
              className="form-select"
              value={filters.action}
              onChange={(e) => updateFilter("action", e.target.value)}
            >
              <option value="">All actions</option>
              {actions.map((action) => (
                <option key={action} value={action}>
                  {formatAction(action)}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label className="form-label">From</label>
            <input
              type="date"
              className="form-control"
              value={filters.date_from}
              onChange={(e) => updateFilter("date_from", e.target.value)}
            />
          </div>

          <div className="col-md-2">
            <label className="form-label">To</label>
            <input
              type="date"
              className="form-control"
              value={filters.date_to}
              onChange={(e) => updateFilter("date_to", e.target.value)}
            />
          </div>

          <div className="col-md-1 d-flex align-items-end">
            <button className="btn btn-dark w-100" type="submit">
              Go
            </button>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-link px-0 mt-2"
          onClick={clearFilters}
        >
          Clear filters
        </button>
      </form>

      <div className="audit-table-card">
        {loading ? (
          <div className="p-4 text-muted">Loading audit logs...</div>
        ) : logs.length === 0 ? (
          <div className="p-4 text-muted">No audit logs found.</div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Inmate</th>
                  <th>Document</th>
                  <th>IP</th>
                  <th>Hash</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="small text-nowrap">{formatDate(log.created_at)}</td>
                    <td>
                      <span className="audit-action-pill">
                        {formatAction(log.action)}
                      </span>
                    </td>
                    <td>
                      <div className="fw-semibold">{log.user?.name || "System"}</div>
                      <div className="text-muted small">{log.user?.email || "—"}</div>
                    </td>
                    <td className="text-muted small">
                      {log.actor_role || log.user?.role || "—"}
                    </td>
                    <td>
                      <div>{getInmateName(log)}</div>
                      <div className="text-muted small">
                        {log.legal_document?.inmate?.inmate_number || ""}
                      </div>
                    </td>
                    <td>
                      <div className="fw-semibold">
                        {log.legal_document?.title || "—"}
                      </div>
                      <div className="text-muted small">
                        {log.legal_document?.status || ""}
                      </div>
                    </td>
                    <td className="small text-muted">{log.ip_address || "—"}</td>
                    <td>
                      {log.entry_hash ? (
                        <span className="audit-hash-good">Chained</span>
                      ) : (
                        <span className="audit-hash-legacy">Legacy</span>
                      )}
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-dark"
                        onClick={() => setSelectedLog(log)}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination && pagination.last_page > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            className="btn btn-outline-dark"
            disabled={pagination.current_page <= 1}
            onClick={() => goToPage(pagination.current_page - 1)}
          >
            Previous
          </button>

          <div className="text-muted small">
            Page {pagination.current_page} of {pagination.last_page}
          </div>

          <button
            className="btn btn-outline-dark"
            disabled={pagination.current_page >= pagination.last_page}
            onClick={() => goToPage(pagination.current_page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {selectedLog && (
        <div className="app-dialog-backdrop" role="presentation">
          <div
            className="audit-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="audit-detail-title"
            tabIndex="-1"
            onKeyDown={(event) => {
              if (event.key === "Escape") setSelectedLog(null);
            }}
          >
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h4 className="fw-bold mb-1" id="audit-detail-title">Audit Event Details</h4>
                <div className="text-muted small">
                  Event #{selectedLog.id} · {formatDate(selectedLog.created_at)}
                </div>
              </div>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setSelectedLog(null)}
                autoFocus
              >
                Close
              </button>
            </div>

            <div className="audit-detail-grid">
              <div>
                <label>Action</label>
                <div>{formatAction(selectedLog.action)}</div>
              </div>

              <div>
                <label>User</label>
                <div>{selectedLog.user?.name || "System"}</div>
              </div>

              <div>
                <label>Email</label>
                <div>{selectedLog.user?.email || "—"}</div>
              </div>

              <div>
                <label>Role</label>
                <div>{selectedLog.actor_role || selectedLog.user?.role || "—"}</div>
              </div>

              <div>
                <label>IP Address</label>
                <div>{selectedLog.ip_address || "—"}</div>
              </div>

              <div>
                <label>Document</label>
                <div>{selectedLog.legal_document?.title || "—"}</div>
              </div>
            </div>

            <div className="mt-4">
              <label className="audit-detail-label">Metadata</label>
              <pre className="audit-json-block">
                {JSON.stringify(safeJson(selectedLog.meta), null, 2)}
              </pre>
            </div>

            <div className="mt-4">
              <label className="audit-detail-label">Hash Chain</label>
              <div className="audit-hash-box">
                <div>
                  <strong>Previous:</strong>{" "}
                  <span>{selectedLog.previous_hash || "Legacy / chain start"}</span>
                </div>
                <div>
                  <strong>Entry:</strong>{" "}
                  <span>{selectedLog.entry_hash || "Legacy event"}</span>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="audit-detail-label">User Agent</label>
              <div className="audit-user-agent">
                {selectedLog.user_agent || "—"}
              </div>
            </div>
          </div>
        </div>
      )}
     </main>
    </div>
  );
}
